<script lang="ts">
export default {
  name: 'ConfirmModal',
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppButton from '@/components/v2/AppButton.vue';

const props = withDefaults(
  defineProps<{
    action: () => Promise<boolean>;
    title?: string;
    subtitle?: string;
    confirmText?: string;
    cancelText?: string;
    dismiss?: () => void;
    /** AppModal size: sm, md, lg, xl. Omit for default (md). */
    size?: 'sm' | 'md' | 'lg' | 'xl';
    submitLoading?: boolean;
    displayOnRender?: boolean;
    isConfirmDisabled?: () => boolean;
    keepOpen?: boolean;
    canClickOutsideToDismiss?: boolean;
    cancelDisabled?: boolean;
    show?: boolean;
  }>(),
  {
    title: 'Confirm',
    subtitle: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    dismiss: (..._payload: unknown[]) => {},
    submitLoading: false,
    displayOnRender: false,
    isConfirmDisabled: () => false,
    keepOpen: false,
    canClickOutsideToDismiss: true,
    cancelDisabled: false,
  },
);

const emit = defineEmits(['open', 'closed']);

const showDialog = ref(false);
const pendingAction = ref(false);

function open() {
  showDialog.value = true;
}

function onConfirm() {
  pendingAction.value = true;
  props
    .action()
    .then((result: boolean) => {
      pendingAction.value = false;
      if (result !== false && !props.keepOpen) {
        document.body.focus();
        showDialog.value = false;
      }
    })
    .catch(() => {
      pendingAction.value = false;
    });
}

function onDismiss() {
  if (props.dismiss) {
    props.dismiss();
  }
  showDialog.value = false;
}

watch(showDialog, (newVal) => {
  if (newVal) {
    emit('open');
  } else {
    emit('closed');
  }
});

watch(
  () => props.show,
  (newVal) => {
    showDialog.value = newVal ?? false;
  },
  { immediate: true },
);

onMounted(() => {
  if (props.displayOnRender) {
    showDialog.value = true;
  }
});
</script>

<template>
  <div class="inline">
    <slot
      name="button"
      :props="{ onClick: open }" />
    <AppModal
      :show="showDialog"
      :title="title"
      :cancel-button-label="cancelText"
      :action-button-label="confirmText"
      :handle-cancel="onDismiss"
      :handle-action="onConfirm"
      :loading="submitLoading || pendingAction"
      :action-disabled="isConfirmDisabled?.() ?? false"
      :size="size">
      <template #title>
        <h1 class="text-xl font-semibold">{{ title }}</h1>
        <p
          v-if="subtitle"
          class="mt-0 text-base text-gray-600">
          {{ subtitle }}
        </p>
      </template>
      <template #content>
        <slot />
      </template>
      <template #actions>
        <slot name="expandedActions" />
        <slot
          name="continueButton"
          :click="() => (showDialog = false)" />
        <AppButton
          v-if="!cancelDisabled"
          @click="onDismiss">
          {{ cancelText }}
        </AppButton>
        <AppButton
          primary
          class="ml-2"
          data-testid="confirm-action"
          @click="onConfirm"
          :loading="submitLoading || pendingAction"
          :disabled="isConfirmDisabled?.() ?? false">
          {{ confirmText }}
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>
